<template>
  <div>
    <ContentDetailGallery :variant="variant" class="float-none md:float-left mr-0 md:mr-5" />
    <div class="flex flex-col-reverse lg:flex-row items-start justify-between gap-2 mb-4 md:mb-8 pt-5 pl-5 md:pl-0 pr-5 md:pr-10">
      <h1 class="font-bold text-2xl tracking-tight text-primary-dark cursor-pointer transition-colors hover:text-primary-dark-hover no-underline hover:underline" @click="navigate">
          {{ localized(variant, 'name') }}
      </h1>
      <div class="w-full lg:w-auto flex items-center justify-end">
        <ContentTagParameters :type="CATEGORY_TYPE.NEWS" :data="variant" />
        <div class="w-full lg:w-auto flex items-center justify-end space-x-2">
          <NewsNotificationButton :variant="variant" class="flex-shrink-0" />
          <NewsFavorite :variant="variant" class="flex-shrink-0" />
        </div>
      </div>
    </div>

    <div v-if="!isAnonymized" class="flex items-center justify-between space-x-2 mb-9 ml-5 md:ml-0 pr-5 md:pr-10">
      <ProfileHeader :data="variant" />
      <NewsShare :variant="variant" />
    </div>

    <p class="text-sml text-body font-normal mb-9 pl-5 md:pl-10 pr-5 md:pr-10">
      {{ localized(variant, 'perex') }}
    </p>

    <article class="prose max-w-none mb-9 pl-5 md:pl-10 pr-5 md:pr-10" v-html="localized(variant, 'content')" />

    <div class="w-full flex flex-row items-center justify-between space-x-2 mb-9 pl-5 md:pl-10 pr-5 md:pr-10">
      <div class="flex flex-row items-center justify-start space-x-2.5">
        <div v-if="variant.publishedAt" class="text-xs">{{ $t('text-published') }}: {{ $luxon.fromISO(variant.publishedAt).setLocale(routeLang.code.value).toLocaleString($luxon.DATE_MED) }}</div>
        <ContentViews :variant="variant" />
        <ContentLikes :variant="variant" />
      </div>
      <NewsNotificationLink class="text-primary-light sm:max-w-fit" />
    </div>

    <div v-if="configs.postMessagesFeature || variant.commentsAllowed" class="px-5 md:px-10 mb-9">
      <ContentMessages v-if="!isAnonymized" :content="variant" />
    </div>

    <NewsDetailJson :variant="variant" />
  </div>
</template>

<script setup>
import _ from 'lodash-es';
import { CATEGORY_TYPE } from "@utils/constants";
import { ROUTES } from "@utils/routes";
import { useConfigStore } from '@stores/config';
import ProfileHeader from "@components/profile/profile-header";
import NewsFavorite from "@components/news/news-details/news-favorite";
import NewsNotificationButton from "@components/news/news-details/news-notification-button";
import NewsNotificationLink from "@components/news/news-details/news-notification-link";
import ContentViews from "@components/content/content-details/content-views";
import ContentLikes from "@components/content/content-details/content-likes";
import ContentDetailGallery from "@components/content/content-details/content-detail-gallery";
import NewsShare from "@components/news/news-details/news-share";
import ContentMessages from "@components/content/content-details/content-messages";
import NewsDetailJson from "@components/news/news-details/news-detail-json";
import ContentTagParameters from "@components/filter/content-tag-parameters";

const props = defineProps({
  variant: {
    type: Object,
    required: true
  },
  size: {
    type: String,
    default: 'page' //page, modal
  }
})

const { $eventBus, $luxon } = useNuxtApp();
const configs = useConfigStore();
const routeLang = useRouteLang();

const routeCode = computed(() => {
  return {
    inspiration: ROUTES.CODE.INSPIRATION,
    news: ROUTES.CODE.NEWS
  }[props.variant?.type] || ROUTES.CODE.PRODUCT;
});

const isAnonymized = computed(() => _.includes([STATUS.HIDDEN, STATUS.CLOSED], props.variant?.status));

const closeModal = () => {
    $eventBus.emit('modal:close');
}

const navigate = async () => {
    closeModal();
    await navigatorTo(translatePath(routeCode.value, slugify(props.variant?.id, props.variant?.name)));
}

</script>
